import React, { useEffect, useMemo, useState } from "react";
import { Sparkles } from "lucide-react";
import api from "@/lib/api";
import { ChallengeCard } from "@/components/landing/marketplace/ChallengeCard";

const REAL_TRACKS = [
  { id: "data_cloud", title: "30-Day Data Cloud Challenge", category: "Salesforce", creatorName: "Cloudivo", creatorVerified: true, difficulty: "All Levels", durationDays: 30, xp: 6000, skills: ["Data Streams", "Identity Resolution", "Segmentation", "Activation"], bossBattle: true, rating: 4.9 },
  { id: "sfmc", title: "30-Day SFMC Challenge", category: "Salesforce", creatorName: "Cloudivo", creatorVerified: true, difficulty: "All Levels", durationDays: 30, xp: 6000, skills: ["Journey Builder", "Automation Studio", "AMPscript"], bossBattle: true, rating: 4.8 },
  { id: "mc_next", title: "30-Day MC Next Challenge", category: "Salesforce", creatorName: "Cloudivo", creatorVerified: true, difficulty: "All Levels", durationDays: 30, xp: 6000, skills: ["DMOs", "Identity Resolution", "Agentforce"], bossBattle: true, rating: 4.8 },
  { id: "admin_dev", title: "30-Day Admin + Dev Challenge", category: "Salesforce", creatorName: "Cloudivo", creatorVerified: true, difficulty: "All Levels", durationDays: 30, xp: 6000, skills: ["Security Model", "Apex", "Automation"], bossBattle: true, rating: 4.9 },
  { id: "servicenow", title: "30-Day ServiceNow Challenge", category: "ServiceNow", creatorName: "Cloudivo", creatorVerified: true, difficulty: "All Levels", durationDays: 30, xp: 6000, skills: ["CMDB", "Flow Designer", "ITSM"], bossBattle: true, rating: 4.8 },
];

const CATEGORIES = ["All", "Salesforce", "ServiceNow"];

export default function AiSkillsHubPage() {
  const [trackStats, setTrackStats] = useState(null);
  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    api.get("/public/track-stats").then((r) => setTrackStats(r.data)).catch(() => {});
  }, []);

  const tracks = useMemo(
    () => REAL_TRACKS.map((t) => ({ ...t, isReal: true, learners: (trackStats && trackStats[t.id]) || 0 })),
    [trackStats]
  );
  const filtered = activeCategory === "All" ? tracks : tracks.filter((t) => t.category === activeCategory);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="ai-skills-hub-page">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-9 h-9 rounded-xl bg-primary/15 border border-primary/25 flex items-center justify-center">
          <Sparkles className="w-4.5 h-4.5 text-primary" />
        </div>
        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary">5 Tracks</span>
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white">AI Skills</h1>
      <p className="text-sm sm:text-base text-slate-400 mt-2 max-w-2xl mb-6">Every 30-day gamified skill track in one place — pick one up where you left off, or start a new one.</p>

      <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-3 mb-6 -mx-1 px-1" data-testid="ai-skills-category-pills">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            data-testid={`ai-skills-category-pill-${cat.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setActiveCategory(cat)}
            className={`shrink-0 text-xs font-semibold px-4 py-2 rounded-full border transition-colors whitespace-nowrap ${
              activeCategory === cat ? "bg-[#5C9DFF] border-[#5C9DFF] text-white" : "bg-white/[0.03] border-white/10 text-slate-300 hover:border-white/25 hover:text-white"
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="ai-skills-track-grid">
        {filtered.map((t, i) => <ChallengeCard key={t.id} challenge={t} index={i} />)}
      </div>

      {filtered.length === 0 && (
        <p className="text-center text-sm text-slate-500 py-14" data-testid="ai-skills-empty-state">No tracks in this category yet.</p>
      )}
    </div>
  );
}
